import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { FiClock, FiX } from 'react-icons/fi';

const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes
const WARNING_BEFORE_TIMEOUT = 5 * 60 * 1000; // Show warning 5 minutes before timeout

function SessionTimeoutWarning() {
  const { user, logout } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const lastActivityRef = useRef(null);

  // Initialize lastActivity on mount
  useEffect(() => {
    lastActivityRef.current = Date.now();
  }, []);

  // Reset activity timer on user interaction
  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    setShowWarning(false);
  }, []);

  // Listen for user activity
  useEffect(() => {
    if (!user) return;

    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    
    const handleActivity = () => {
      resetTimer();
    };

    events.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
    };
  }, [user, resetTimer]);

  // Check for timeout
  useEffect(() => {
    if (!user || lastActivityRef.current === null) return;

    const checkTimeout = () => {
      if (lastActivityRef.current === null) return;
      
      const now = Date.now();
      const timeSinceActivity = now - lastActivityRef.current;
      const timeUntilTimeout = INACTIVITY_TIMEOUT - timeSinceActivity;

      if (timeUntilTimeout <= 0) {
        // Session expired due to inactivity
        logout();
        window.location.href = '/?session_timeout=true';
      } else if (timeUntilTimeout <= WARNING_BEFORE_TIMEOUT) {
        // Show warning
        setShowWarning(true);
        setTimeLeft(Math.ceil(timeUntilTimeout / 1000));
      } else {
        setShowWarning(false);
      }
    };

    // Check every second
    const interval = setInterval(checkTimeout, 1000);
    
    return () => clearInterval(interval);
  }, [user, logout]);

  // Update countdown
  useEffect(() => {
    if (!showWarning) return;

    const countdown = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          logout();
          window.location.href = '/?session_timeout=true';
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(countdown);
  }, [showWarning, logout]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleStayLoggedIn = () => {
    resetTimer();
  };

  if (!user || !showWarning) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl animate-fade-in-up">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center">
              <FiClock className="text-amber-500" size={24} />
            </div>
            <div>
              <h3 className="font-semibold text-foreground text-lg">Session Timeout Warning</h3>
              <p className="text-muted-foreground text-sm">You&apos;ve been inactive</p>
            </div>
          </div>
          <button
            onClick={handleStayLoggedIn}
            className="p-1 text-muted-foreground hover:text-foreground"
          >
            <FiX size={20} />
          </button>
        </div>

        <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 mb-6">
          <p className="text-amber-200 text-center">
            Your session will expire in
          </p>
          <p className="text-3xl font-bold text-amber-500 text-center mt-2">
            {formatTime(timeLeft)}
          </p>
        </div>

        <p className="text-muted-foreground text-sm text-center mb-6">
          Click anywhere or press any key to stay logged in. Otherwise, you&apos;ll be logged out automatically.
        </p>

        <div className="flex gap-3">
          <button
            onClick={handleStayLoggedIn}
            className="flex-1 bg-primary text-primary-foreground py-3 px-4 rounded-xl font-medium hover:bg-primary/90 transition-colors"
          >
            Stay Logged In
          </button>
          <button
            onClick={() => {
              logout();
              window.location.href = '/';
            }}
            className="px-4 py-3 bg-secondary text-secondary-foreground rounded-xl font-medium hover:bg-secondary/80 transition-colors"
          >
            Log Out
          </button>
        </div>
      </div>
    </div>
  );
}

export default SessionTimeoutWarning;
